import { useState } from 'react'
import type { JSX } from 'react'
import { formatInstant } from '../format.js'
import { pathFor } from '../routing.js'
import { Outputs } from './outputs.jsx'
import { useAccountRunFacts } from './account-run-facts.js'
import { InstanceHead } from './instance-head.jsx'
import { InstallerLabel } from './installer.jsx'
import { DiagnosticBundleButton } from './diagnostic-bundle.jsx'
import { Debug } from './debug.jsx'
import { Modal } from './modal.jsx'
import { RunProgress } from './run-progress.jsx'
import { StartRun } from './start-run.jsx'
import { BoundaryPanel } from './boundary-panel.jsx'
import { StateAccessPanel } from './state-access-panel.jsx'
import { RunState } from './status.jsx'
import { RunSummaryHeadlineText } from './run-summary.jsx'
import { AwaitingHint } from './approvals.jsx'
import type { TenantKind } from '@qim/shared'
import { fetchPreflight } from '../api.js'
import type { DeploymentDetail, RunSummary } from '../api.js'
import type { DeploymentTab } from '../routing.js'

/**
 * One deployment, and the record that replaces asking whoever ran it last.
 *
 * The history is the run list, because that *is* the history — there is no
 * separate changelog to fall out of step with it.
 */
export const Deployment = ({
  baseUrl,
  tenantId,
  role,
  environment,
  deployment,
  onOpen,
  onChanged,
  kind,
  requesterIsStaff = true,
  tab = 'overview',
}: {
  /** Which of the instance's tabs the path names (IM-199). */
  readonly tab?: DeploymentTab
  readonly baseUrl: string
  readonly tenantId: string
  readonly role: string
  /** Which console this is, for the run form's defaults (IM-175). */
  readonly environment: string
  readonly deployment: DeploymentDetail
  /**
   * The customer's kind, for the protection lock (IM-39, IM-174): only Qrvey's
   * own instances take one. Absent while the tenant list is arriving, which
   * offers no lock rather than one the API would refuse.
   */
  readonly kind?: TenantKind | undefined
  /** Staff or the customer, for what the run form says will wait (IM-102). */
  readonly requesterIsStaff?: boolean
  /** Navigates in place, so a history row can reach that run's own screen. */
  readonly onOpen: (path: string) => void
  /** Re-reads this deployment, so a run that just started is part of it. */
  readonly onChanged: () => void
}): JSX.Element => {
  const [started, setStarted] = useState<RunSummary | undefined>(undefined)

  /**
   * Same reason as the customer screen (IM-117), with one more payoff here.
   *
   * The run this screen just started is already recorded with the variables it
   * carried, so re-reading makes the form offer them back to the apply that
   * follows a plan — and it puts the new run in the history table below rather
   * than leaving a screen that says nothing happened.
   */
  const [runPanelOpen, setRunPanelOpen] = useState(false)
  /**
   * Whether this deployment's own region supplies the registry key (IM-118).
   *
   * The form asks the question of the region a run will actually go to, and
   * here that is fixed — this deployment's recorded region, which is also the
   * one the API will use. Reading the whole account's answer and letting the
   * form pick keeps that decision in one place rather than two.
   */
  const { registryFromAccount, installerImages, installerChoices } = useAccountRunFacts(
    baseUrl,
    tenantId,
    deployment.accountKey,
    runPanelOpen,
  )

  const onRunStarted = (run: RunSummary): void => {
    // The dialog closes so the progress panel behind it is what the caller is
    // left looking at — a run's first seconds are the thing worth watching, and
    // a form still covering them is the wrong answer to "did that work".
    setRunPanelOpen(false)
    setStarted(run)
    onChanged()
  }

  const head = (
    <InstanceHead
      deployment={deployment}
      baseUrl={baseUrl}
      tenantId={tenantId}
      role={role}
      onChanged={onChanged}
      kind={kind}
      onOpen={onOpen}
    />
  )

  /*
   * The Debug tab (IM-199): the standard checks, and the diagnostic bundle
   * beside them — both are what somebody reaches for when an instance is
   * misbehaving, and neither belongs on the overview of one that is fine.
   */
  if (tab === 'debug') {
    return (
      <>
        {head}
        <Debug
          baseUrl={baseUrl}
          tenantId={tenantId}
          accountKey={deployment.accountKey}
          deploymentId={deployment.deploymentId}
        />
        {/* Offered only to the roles that may collect one (IM-54). */}
        <DiagnosticBundleButton
          baseUrl={baseUrl}
          tenantId={tenantId}
          accountKey={deployment.accountKey}
          deploymentId={deployment.deploymentId}
          role={role}
        />
      </>
    )
  }

  return (
    <>
      {head}

      {/* A run waiting for approval (IM-102, IM-39) changes what Destroy will
        do, so it sits above the run button. The protection mark is the lock
        beside the name in the head above (IM-174). */}
      {deployment.awaitingApproval === undefined ? null : (
        <div className="approval-note">
          <AwaitingHint deployment={deployment} />
        </div>
      )}

      <Outputs
        baseUrl={baseUrl}
        tenantId={tenantId}
        deploymentId={deployment.deploymentId}
        /* So the panel can name the state object a withheld secret is read
          from. Both are already on this record; neither is a new read. */
        accountKey={deployment.accountKey}
        {...(deployment.region === undefined ? {} : { region: deployment.region })}
        {...(deployment.outputs === undefined ? {} : { initial: deployment.outputs })}
      />

      <section className="panel">
        {/*
          Opening the panel is what asks the account about itself (IM-118).
          This screen otherwise makes no cross-account call, and it should keep
          making none until someone actually goes to start a run — the same
          reason the customer screen gates its own read, one panel per account.
        */}
        {/*
          A modal, and opening it is what asks the account about itself
          (IM-118, IM-146). This screen otherwise makes no cross-account call
          and should keep making none until somebody actually goes to start a
          run — the disclosure gave that for free by not mounting, and so does
          rendering the dialog only when it is open.
        */}
        <button type="button" className="primary" onClick={() => setRunPanelOpen(true)}>
          Run something against this deployment
        </button>
        {runPanelOpen ? (
          <Modal
            title="Run something against this deployment"
            onCancel={() => setRunPanelOpen(false)}
            wide
          >
            <StartRun
              baseUrl={baseUrl}
              tenantId={tenantId}
              accountKey={deployment.accountKey}
              role={role}
              environment={environment}
              deploymentId={deployment.deploymentId}
              /* So is the region, for the same reason and a worse consequence: a
                run in another region would not find this deployment's state and
                would build a second instance rather than update this one. */
              deployments={[
                {
                  deploymentId: deployment.deploymentId,
                  status: deployment.status,
                  ...(deployment.region === undefined ? {} : { region: deployment.region }),
                  // So an apply started from here offers the plan's own answers.
                  ...(deployment.variables === undefined
                    ? {}
                    : { variables: deployment.variables }),
                  ...(deployment.protected === undefined
                    ? {}
                    : { protected: deployment.protected }),
                  ...(deployment.protectionRemovedByYou === undefined
                    ? {}
                    : { protectionRemovedByYou: deployment.protectionRemovedByYou }),
                  ...(deployment.boundary === undefined
                    ? {}
                    : { boundary: { arn: deployment.boundary.arn } }),
                },
              ]}
              requesterIsStaff={requesterIsStaff}
              onOpen={onOpen}
              {...(registryFromAccount === undefined ? {} : { registryFromAccount })}
              {...(installerImages === undefined ? {} : { installerImages })}
              {...(installerChoices === undefined ? {} : { installerChoices })}
              /* Asked only because the dialog is open, like the account's own
                facts above (IM-36). */
              preflight={(command, variables, region) =>
                fetchPreflight(baseUrl, tenantId, deployment.accountKey, command, variables, region)
              }
              /* What is running now, so an apply that changes it can be called
                what it is. See `deployedVersion` in `start-run.tsx`. */
              {...(deployment.chartVersion === undefined
                ? {}
                : { deployedVersion: deployment.chartVersion })}
              onStarted={onRunStarted}
            />
          </Modal>
        ) : null}
        {started === undefined ? null : (
          <RunProgress baseUrl={baseUrl} tenantId={tenantId} run={started} />
        )}
      </section>

      {/* What every run of this instance is bounded by (IM-192). From the record;
        the account is asked only when the check is opened. */}
      <BoundaryPanel
        baseUrl={baseUrl}
        tenantId={tenantId}
        accountKey={deployment.accountKey}
        deploymentId={deployment.deploymentId}
        recorded={deployment.boundary}
        /* Newest first: the newest apply that reached the account, failed or not —
          a failed one can still have recreated a role. */
        {...(() => {
          const apply = deployment.history.find(
            (run) =>
              run.command === 'apply' && (run.state === 'succeeded' || run.state === 'failed'),
          )
          return apply === undefined ? {} : { lastApplyAt: apply.requestedAt }
        })()}
        role={role}
        onChanged={onChanged}
      />

      {/* State a scoped deploy role cannot reach by itself (IM-214). From the
        record; the template is fetched only when downloaded. */}
      <StateAccessPanel
        baseUrl={baseUrl}
        tenantId={tenantId}
        accountKey={deployment.accountKey}
        deploymentId={deployment.deploymentId}
        stateBucket={deployment.stateBucket}
        stateKey={deployment.stateKey}
      />

      <section className="panel">
        <h2>History</h2>
        {deployment.history.length === 0 ? (
          /*
           * An adopted instance has no runs here (IM-140), and an empty table
           * under five column headings reads as a screen that failed to load.
           * Saying where it came from answers the question the emptiness
           * raises.
           */
          <p className="muted">
            Nothing has been run against this instance from here.
            {deployment.adopted === undefined ? null : (
              <>
                {' '}
                It was brought in by {deployment.adopted.by} on{' '}
                <time dateTime={deployment.adopted.at}>{formatInstant(deployment.adopted.at)}</time>
                , with the state location from its <code>config.json</code>.
              </>
            )}
          </p>
        ) : null}
        {/* Five columns, so it scrolls in its own box rather than off the
          viewport edge. */}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">What</th>
                <th scope="col">Who</th>
                <th scope="col">Version</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {deployment.history.map((run) => (
                <tr key={run.runId}>
                  <td>
                    <time dateTime={run.requestedAt}>{formatInstant(run.requestedAt)}</time>
                  </td>
                  <td>{run.command}</td>
                  <td>{run.requestedBy}</td>
                  <td>
                    {run.chartVersion ?? '—'}
                    {/* Which installer ran it, when it was recorded (IM-195). */}
                    {run.installerImage === undefined ? null : (
                      <p className="muted">
                        installer{' '}
                        <InstallerLabel
                          image={run.installerImage}
                          digest={run.installerImageDigest}
                        />
                      </p>
                    )}
                  </td>
                  <td>
                    <RunState state={run.state} />
                    {/* The reason a run failed belongs next to the run, not in a log
                    someone has to know to look for. */}
                    {run.failure === undefined ? null : (
                      <p className="muted">{run.failure.detail}</p>
                    )}
                    {/* From the record, so a row costs no read (IM-35). Present
                      once somebody has opened the finished run. */}
                    {run.summaryHeadline === undefined ? null : (
                      <RunSummaryHeadlineText headline={run.summaryHeadline} />
                    )}
                    {/* Out to the run's own screen rather than a log nested
                      in this cell. That is what gives a run an address someone
                      can paste, and it removes the disclosure-inside-a-
                      disclosure whose mounting IM-83 had to work around. */}
                    <a
                      href={pathFor({ screen: 'run', tenantId, runId: run.runId })}
                      onClick={(event) => {
                        if (
                          event.metaKey ||
                          event.ctrlKey ||
                          event.shiftKey ||
                          event.button !== 0
                        ) {
                          return
                        }
                        event.preventDefault()
                        onOpen(pathFor({ screen: 'run', tenantId, runId: run.runId }))
                      }}
                    >
                      Open run
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}
